import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Slider } from '../../components/ui/slider';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  Play, Pause, Volume2, VolumeX, ExternalLink, Monitor, Tablet, Smartphone,
  Search, ChevronRight, ChevronUp, ChevronDown, Star, Quote,
  Target, TrendingUp, Phone, Mail, ArrowUp, ArrowDown, Sparkles, ArrowRight, Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import { SiteHeader, SiteFooter, AGENCY_CONTACT } from '../../components/website/SharedLayout';
import { toast } from 'sonner';
import api from '../../lib/axios';

// Full portfolio data with testimonials
const PORTFOLIO = [
  {
    id: 1,
    title: "Burger King - Campagne locale",
    client: "Burger King",
    clientLogo: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=200&h=200&fit=crop",
    category: "graphique",
    description: "Conception et déploiement d'une campagne de communication locale pour l'ouverture d'un nouveau restaurant. Création d'affiches, flyers, habillage vitrine et supports digitaux.",
    image: "https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800",
    tags: ["Campagne", "Print", "Digital"],
    slug: "burger-king-campagne-locale",
    objectives: ["Générer du trafic pour l'ouverture", "Créer de la notoriété locale", "Cibler les familles et jeunes actifs"],
    results: ["+200% de visites le jour d'ouverture", "15 000 flyers distribués", "Couverture presse locale", "File d'attente de 2h à l'ouverture"],
    testimonial: {
      type: "text",
      author: "Jean-Marc Dubois",
      role: "Directeur de franchise",
      content: "L'équipe MSC a parfaitement compris nos enjeux locaux. La campagne a dépassé toutes nos attentes avec une affluence record le jour J !",
      rating: 5
    }
  },
  {
    id: 2,
    title: "Orpi - Identité visuelle agence",
    client: "Orpi Istres",
    clientLogo: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=200&h=200&fit=crop",
    category: "graphique",
    description: "Refonte complète de l'identité visuelle d'une agence immobilière avec supports print et digitaux. Création de templates personnalisables pour une communication cohérente.",
    image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800",
    tags: ["Logo", "Charte graphique", "Signalétique"],
    slug: "orpi-identite-visuelle",
    objectives: ["Moderniser l'image de l'agence", "Se différencier de la concurrence locale", "Créer des supports facilement déclinables"],
    results: ["+45% de contacts entrants", "Reconnaissance de marque améliorée", "Cohérence sur tous les supports"],
    testimonial: {
      type: "text",
      author: "Sophie Martin",
      role: "Gérante d'agence",
      content: "Notre nouvelle identité visuelle nous a permis de nous repositionner sur le marché local. Les retours clients sont unanimes : notre image est devenue premium.",
      rating: 5
    }
  },
  {
    id: 3,
    title: "Lions Club - Site événementiel",
    client: "Lions Club Istres",
    clientLogo: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=200&h=200&fit=crop",
    category: "web",
    description: "Création d'un site web responsive pour la gestion des événements caritatifs et des adhésions du club. Intégration d'un système de paiement en ligne.",
    image: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800",
    fullImage: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800&h=2400&fit=crop",
    websiteUrl: "https://lions-club-istres.fr",
    tags: ["Site web", "Responsive", "E-commerce"],
    slug: "lions-club-site-evenementiel",
    objectives: ["Digitaliser la gestion des événements", "Simplifier les inscriptions", "Augmenter la visibilité des actions"],
    results: ["+150% d'inscriptions en ligne", "Temps de gestion divisé par 3", "20 000€ de dons collectés via le site"],
    testimonial: {
      type: "video",
      author: "Pierre Lefebvre",
      role: "Président du Club",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      content: "Le site a révolutionné notre gestion événementielle. Un vrai gain de temps pour toute l'équipe !",
      rating: 5
    }
  },
  {
    id: 4,
    title: "IPHB - Vidéo partenariat sportif",
    client: "IPHB Basket",
    clientLogo: "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=200&h=200&fit=crop",
    category: "video",
    description: "Réalisation d'une vidéo corporate de présentation du partenariat sportif et des actions sociales menées par le club auprès des jeunes.",
    image: "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800",
    videoUrl: "https://assets.mixkit.co/videos/preview/mixkit-basketball-player-scoring-a-goal-511-large.mp4",
    tags: ["Vidéo corporate", "Montage", "Interview"],
    slug: "iphb-video-partenariat",
    objectives: ["Promouvoir les valeurs du club", "Attirer de nouveaux sponsors", "Valoriser l'impact social"],
    results: ["50 000 vues sur les réseaux", "3 nouveaux sponsors acquis", "Diffusion TV régionale"],
    testimonial: {
      type: "text",
      author: "Marc Dupuis",
      role: "Président IPHB",
      content: "Cette vidéo a été un véritable levier pour notre développement. La qualité de production a impressionné tous nos partenaires.",
      rating: 5
    }
  },
  {
    id: 5,
    title: "Restaurant Le Provençal - Click & Collect",
    client: "Le Provençal",
    clientLogo: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=200&h=200&fit=crop",
    category: "web",
    description: "Développement d'une boutique en ligne complète pour la vente de produits locaux et un système de click & collect innovant.",
    image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800",
    fullImage: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&h=2400&fit=crop",
    websiteUrl: "https://le-provencal-example.com",
    tags: ["E-commerce", "Click & Collect", "Paiement en ligne"],
    slug: "restaurant-provencal-ecommerce",
    objectives: ["Diversifier les revenus pendant le COVID", "Digitaliser les commandes", "Fidéliser la clientèle"],
    results: ["+80% de CA pendant le confinement", "2000+ commandes traitées", "Note client 4.8/5"],
    testimonial: {
      type: "text",
      author: "Antoine Blanc",
      role: "Chef & Propriétaire",
      content: "Le site click & collect nous a littéralement sauvés pendant la crise. L'interface est intuitive pour nous comme pour nos clients.",
      rating: 5
    }
  },
  {
    id: 6,
    title: "Radio Locale - Habillage Sonore",
    client: "Radio Sud FM",
    clientLogo: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=200&h=200&fit=crop",
    category: "audio",
    description: "Création de l'habillage sonore complet de la radio : jingles, virgules, génériques et identité musicale. Production professionnelle avec mixage et mastering.",
    image: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=800",
    audioUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3",
    tags: ["Jingle", "Habillage", "Radio"],
    slug: "radio-sud-habillage",
    objectives: ["Créer une identité sonore unique", "Moderniser l'image de la radio", "Produire des éléments modulables"],
    results: ["12 jingles produits", "Identité sonore cohérente 24h/24", "+25% d'audience sur la tranche matinale"],
    testimonial: {
      type: "audio",
      author: "François Bernard",
      role: "Directeur d'antenne",
      audioUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3",
      content: "L'habillage sonore signé MSC a complètement transformé notre antenne. Nos auditeurs nous le disent chaque jour !",
      rating: 5
    }
  },
  {
    id: 7,
    title: "Podcast Business Talks",
    client: "Business Talks",
    clientLogo: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=200&h=200&fit=crop",
    category: "audio",
    description: "Production et post-production d'un podcast hebdomadaire sur l'entrepreneuriat. Montage, mixage, sound design et distribution sur toutes les plateformes.",
    image: "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=800",
    audioUrl: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3",
    tags: ["Podcast", "Montage", "Distribution"],
    slug: "business-talks-podcast",
    objectives: ["Lancer un podcast professionnel", "Assurer une qualité studio", "Distribuer sur toutes les plateformes"],
    results: ["50 épisodes produits", "10 000 écoutes/mois", "Top 50 Business France sur Apple Podcasts"],
    testimonial: {
      type: "text",
      author: "Claire Dumont",
      role: "Fondatrice Business Talks",
      content: "MSC gère toute la production technique, ce qui me permet de me concentrer sur le contenu. La qualité est irréprochable.",
      rating: 5
    }
  },
  {
    id: 8,
    title: "Tutoriels SaaS - Vidéo YouTube",
    client: "CloudApp Pro",
    clientLogo: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=200&h=200&fit=crop",
    category: "video",
    description: "Série de tutoriels vidéo pour YouTube avec captures d'écran, animations et voix off professionnelle. Optimisation SEO YouTube incluse.",
    image: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=800",
    youtubeUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    tags: ["YouTube", "Tutoriel", "SaaS"],
    slug: "cloudapp-tutoriels",
    objectives: ["Réduire les tickets support", "Améliorer l'onboarding utilisateur", "Créer une chaîne YouTube active"],
    results: ["250 000 vues totales", "+500 abonnés/mois", "Réduction support de 30%"],
    testimonial: {
      type: "video",
      author: "Thomas Morel",
      role: "CEO CloudApp Pro",
      videoUrl: "https://www.youtube.com/embed/dQw4w9WgXcQ",
      content: "Les tutoriels vidéo ont eu un impact direct sur notre taux de rétention. Un investissement très rentable !",
      rating: 5
    }
  }
];

// Enhanced Audio Player with seek functionality
const AudioPlayer = ({ audioUrl, coverUrl, title }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const audioRef = useRef(null);

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (value) => {
    if (!audioRef.current) return;
    const newTime = (value[0] / 100) * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (value) => {
    if (!audioRef.current) return;
    const newVolume = value[0] / 100;
    audioRef.current.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.volume = volume || 0.8;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  return (
    <div className="rounded-xl overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 p-6">
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onEnded={() => setIsPlaying(false)}
      />
      
      <div className="flex items-center gap-6">
        {coverUrl && (
          <div className="relative">
            <img src={coverUrl} alt={title} className="w-24 h-24 rounded-xl object-cover shadow-lg" />
            {isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-full h-full bg-black/30 rounded-xl flex items-center justify-center">
                  <div className="flex items-end gap-1 h-8">
                    {[40, 65, 50, 80].map((height, i) => (
                      <div 
                        key={i} 
                        className="w-1.5 bg-cyan-400 rounded-full animate-pulse"
                        style={{ height: `${height}%`, animationDelay: `${i * 0.15}s` }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        
        <div className="flex-1">
          <h4 className="font-semibold text-white text-lg mb-4">{title}</h4>
          
          {/* Progress Bar with Seek */}
          <div className="mb-4">
            <Slider
              value={[duration ? (currentTime / duration) * 100 : 0]}
              max={100}
              step={0.1}
              onValueChange={handleSeek}
              className="cursor-pointer"
            />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-slate-400">{formatTime(currentTime)}</span>
              <span className="text-xs text-slate-400">{formatTime(duration)}</span>
            </div>
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-4">
            {/* Play/Pause */}
            <Button
              size="icon"
              className={cn(
                "w-12 h-12 rounded-full shadow-lg transition-all",
                isPlaying ? "bg-cyan-500 hover:bg-cyan-600" : "bg-white hover:bg-slate-100"
              )}
              onClick={togglePlay}
            >
              {isPlaying ? (
                <Pause className="w-5 h-5 text-white" />
              ) : (
                <Play className="w-5 h-5 text-slate-900 ml-0.5" />
              )}
            </Button>
            
            {/* Volume */}
            <div className="flex items-center gap-2 flex-1 max-w-[150px]">
              <button onClick={toggleMute} className="text-slate-400 hover:text-white transition-colors">
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </button>
              <Slider
                value={[isMuted ? 0 : volume * 100]}
                max={100}
                step={1}
                onValueChange={handleVolumeChange}
                className="cursor-pointer"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Video Player Component
const VideoPlayer = ({ videoUrl, youtubeUrl, poster }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const getYouTubeId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  if (youtubeUrl) {
    const videoId = getYouTubeId(youtubeUrl);
    return (
      <div className="relative aspect-video rounded-xl overflow-hidden bg-black">
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}`}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="Video"
        />
      </div>
    );
  }

  return (
    <div className="relative aspect-video rounded-xl overflow-hidden bg-black group">
      <video
        ref={videoRef}
        src={videoUrl}
        poster={poster}
        autoPlay
        muted={isMuted}
        loop
        playsInline
        className="w-full h-full object-cover"
      />
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="flex items-center justify-between">
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/20"
            onClick={() => {
              if (videoRef.current) {
                if (isPlaying) videoRef.current.pause();
                else videoRef.current.play();
                setIsPlaying(!isPlaying);
              }
            }}
          >
            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/20"
            onClick={() => setIsMuted(!isMuted)}
          >
            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Website Preview with Scroll Controls
const WebsitePreview = ({ screenshot, websiteUrl, title }) => {
  const [device, setDevice] = useState('desktop');
  const scrollRef = useRef(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !isAutoScrolling) return;

    intervalRef.current = setInterval(() => {
      if (container.scrollTop < container.scrollHeight - container.clientHeight) {
        container.scrollTop += 1;
      } else {
        container.scrollTop = 0;
      }
    }, 50);

    return () => clearInterval(intervalRef.current);
  }, [device, isAutoScrolling]);

  const scrollUp = () => {
    if (scrollRef.current) {
      setIsAutoScrolling(false);
      scrollRef.current.scrollBy({ top: -100, behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    if (scrollRef.current) {
      setIsAutoScrolling(false);
      scrollRef.current.scrollBy({ top: 100, behavior: 'smooth' });
    }
  };

  return (
    <div className="bg-slate-100 rounded-xl p-6">
      {/* Device Selector */}
      <div className="flex justify-center gap-2 mb-6">
        {[
          { id: 'desktop', icon: Monitor, label: 'Desktop' },
          { id: 'tablet', icon: Tablet, label: 'Tablette' },
          { id: 'mobile', icon: Smartphone, label: 'Mobile' }
        ].map(d => {
          const Icon = d.icon;
          return (
            <Button
              key={d.id}
              size="sm"
              variant={device === d.id ? 'default' : 'outline'}
              onClick={() => { setDevice(d.id); setIsAutoScrolling(true); }}
              className={cn("gap-2", device === d.id && "bg-[#06b6d4] hover:bg-[#0891b2]")}
            >
              <Icon className="w-4 h-4" />
              <span className="hidden sm:inline">{d.label}</span>
            </Button>
          );
        })}
      </div>

      {/* Device Mockup with Scroll Controls */}
      <div className="flex justify-center items-center gap-4">
        {/* Scroll Up Button */}
        <Button
          size="icon"
          variant="outline"
          onClick={scrollUp}
          className="rounded-full hidden md:flex"
        >
          <ChevronUp className="w-5 h-5" />
        </Button>

        <div className={cn(
          "bg-slate-900 rounded-2xl overflow-hidden shadow-2xl transition-all duration-300",
          device === 'desktop' && "w-full max-w-2xl",
          device === 'tablet' && "w-80",
          device === 'mobile' && "w-48"
        )}>
          {/* Browser Chrome */}
          <div className="bg-slate-800 px-4 py-3 flex items-center gap-2">
            <div className="flex gap-1.5">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
            <div className="flex-1 bg-slate-700 rounded-lg px-4 py-1.5 text-xs text-slate-400 truncate">
              {websiteUrl || title}
            </div>
          </div>

          {/* Screenshot with scroll */}
          <div
            ref={scrollRef}
            className={cn(
              "overflow-hidden",
              device === 'desktop' && "h-80",
              device === 'tablet' && "h-96",
              device === 'mobile' && "h-80"
            )}
            onMouseEnter={() => setIsAutoScrolling(false)}
            onMouseLeave={() => setIsAutoScrolling(true)}
          >
            <img src={screenshot} alt={title} className="w-full" />
          </div>
        </div>

        {/* Scroll Down Button */}
        <Button
          size="icon"
          variant="outline"
          onClick={scrollDown}
          className="rounded-full hidden md:flex"
        >
          <ChevronDown className="w-5 h-5" />
        </Button>
      </div>

      {/* Mobile Scroll Controls */}
      <div className="flex justify-center gap-4 mt-4 md:hidden">
        <Button size="sm" variant="outline" onClick={scrollUp} className="gap-2">
          <ArrowUp className="w-4 h-4" /> Haut
        </Button>
        <Button size="sm" variant="outline" onClick={scrollDown} className="gap-2">
          <ArrowDown className="w-4 h-4" /> Bas
        </Button>
      </div>

      {/* External Link */}
      {websiteUrl && (
        <div className="flex justify-center mt-6">
          <Button
            variant="outline"
            onClick={() => window.open(websiteUrl, '_blank')}
            className="gap-2"
          >
            <ExternalLink className="w-4 h-4" />
            Voir le site en ligne
          </Button>
        </div>
      )}
    </div>
  );
};

// Testimonial Component
const TestimonialDisplay = ({ testimonial }) => {
  if (!testimonial) return null;

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-6 border border-slate-200">
      <div className="flex items-start gap-4 mb-4">
        <Quote className="w-10 h-10 text-[#06b6d4] flex-shrink-0" />
        <div>
          <h4 className="font-bold text-slate-900">Témoignage client</h4>
          <div className="flex items-center gap-1 mt-1">
            {[...Array(testimonial.rating || 5)].map((_, i) => (
              <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
            ))}
          </div>
        </div>
      </div>

      {/* Content based on type */}
      {testimonial.type === 'video' && testimonial.videoUrl && (
        <div className="mb-4 rounded-lg overflow-hidden aspect-video">
          <iframe
            src={testimonial.videoUrl}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Témoignage vidéo"
          />
        </div>
      )}

      {testimonial.type === 'audio' && testimonial.audioUrl && (
        <div className="mb-4">
          <AudioPlayer
            audioUrl={testimonial.audioUrl}
            title="Témoignage audio"
          />
        </div>
      )}

      <blockquote className="text-slate-600 italic mb-4">
        &ldquo;{testimonial.content}&rdquo;
      </blockquote>

      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#06b6d4] to-cyan-600 flex items-center justify-center text-white font-bold">
          {testimonial.author?.charAt(0)}
        </div>
        <div>
          <p className="font-semibold text-slate-900">{testimonial.author}</p>
          <p className="text-sm text-slate-500">{testimonial.role}</p>
        </div>
      </div>
    </div>
  );
};

// Background Carousel Component for Hero
const BackgroundCarousel = ({ projects }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Use fallback images if not enough projects
  const fallbackImages = [
    'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=800',
    'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800',
    'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=800',
    'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=800',
    'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800',
    'https://images.unsplash.com/photo-1478737270239-2f02b77fc618?w=800',
  ];
  
  const carouselImages = projects?.length >= 3 
    ? projects.map(p => p.image) 
    : fallbackImages;
  
  useEffect(() => {
    if (carouselImages.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % carouselImages.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, [carouselImages.length]);
  
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* Base gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a365d] via-[#1e3a5f] to-[#2d4a7c]" />
      
      {/* Animated sliding images */}
      <div className="absolute inset-0 flex animate-carousel-slide opacity-30">
        {[...carouselImages, ...carouselImages].map((image, idx) => (
          <div 
            key={idx}
            className="flex-shrink-0 w-1/3 md:w-1/4 h-full p-2 md:p-4"
          >
            <div className="w-full h-full rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src={image} 
                alt=""
                className="w-full h-full object-cover grayscale"
              />
            </div>
          </div>
        ))}
      </div>
      
      {/* Overlay gradients for depth and text readability */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#1a365d] via-[#1a365d]/70 to-[#1a365d]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1a365d] via-transparent to-[#1a365d]/80" />
      
      {/* Decorative circles */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
    </div>
  );
};

const PortfolioPage = ({ noLayout = false }) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('Tous');
  const [subFilter, setSubFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredProject, setHoveredProject] = useState(null);
  
  // Callback modal state
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [callbackForm, setCallbackForm] = useState({ name: '', phone: '', message: '' });
  const [submittingCallback, setSubmittingCallback] = useState(false);
  const [siteSettings, setSiteSettings] = useState(null);
  
  // External lead management endpoint
  const LEAD_API_ENDPOINT = 'https://portfolio-preview-104.preview.emergentagent.com/api/leads/embed';
  
  // Handle callback action - redirect or open modal
  const handleCallbackAction = () => {
    const leadPages = siteSettings?.lead_pages;
    if (leadPages?.use_external_pages && leadPages?.callback_page_url) {
      window.open(leadPages.callback_page_url, '_blank');
    } else {
      setShowCallbackModal(true);
    }
  };
  
  // Handle callback form submission
  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    setSubmittingCallback(true);
    
    try {
      const response = await fetch(LEAD_API_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...callbackForm,
          form_type: 'callback',
          source: 'portfolio_page',
          page_url: window.location.href
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast.success('✅ Demande envoyée ! Nous vous rappelons très vite.');
        setShowCallbackModal(false);
        setCallbackForm({ name: '', phone: '', message: '' });
      } else {
        throw new Error(result.message || 'Erreur');
      }
    } catch (error) {
      console.error('Error submitting callback:', error);
      toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
    } finally {
      setSubmittingCallback(false);
    }
  };
  
  // Load site settings for lead pages configuration
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await api.get('/cms/site-settings');
        if (res.data) {
          setSiteSettings(res.data);
        }
      } catch (error) {
        console.error('Error loading site settings:', error);
      }
    };
    loadSettings();
  }, []);
  
  // Load CMS portfolio data
  useEffect(() => {
    const loadPortfolio = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/cms/portfolio`);
        const data = await res.json();
        if (data && data.length > 0) {
          // Helper function to fix URLs from old domains or relative paths
          const fixImageUrl = (url) => {
            if (!url) return null;
            
            // Handle relative paths starting with /api/uploads/
            if (url.startsWith('/api/uploads/')) {
              return `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${url}`;
            }
            
            // Handle URLs containing /api/uploads/ from old domains
            if (url.includes('/api/uploads/')) {
              const match = url.match(/\/api\/uploads\/[^\s"']+/);
              if (match) {
                return `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${match[0]}`;
              }
            }
            
            // Handle relative paths
            if (url.startsWith('/')) {
              return `${process.env.REACT_APP_BACKEND_URL || window.location.origin}${url}`;
            }
            
            // Handle absolute URLs - return as is
            if (url.startsWith('http')) return url;
            
            // Handle other cases
            return `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/${url}`;
          };
          
          const cmsProjects = data.map(p => ({
            ...p,
            image: p.image ? fixImageUrl(p.image) : null,
            clientLogo: p.client_logo ? fixImageUrl(p.client_logo) : null,
          }));
          // Use ONLY CMS data - do not merge with static data
          setProjects(cmsProjects);
        } else {
          // Fallback to static data only if CMS is empty
          setProjects(PORTFOLIO);
        }
      } catch (error) {
        console.error('Error loading portfolio:', error);
        // Fallback to static data on error
        setProjects(PORTFOLIO);
      } finally {
        setLoading(false);
      }
    };
    loadPortfolio();
  }, []);

  // Category mapping with icons and colors
  const CATEGORIES = {
    'Tous': { color: 'bg-slate-500', count: projects.length },
    'Graphique': { color: 'bg-pink-500', count: projects.filter(p => p.category === 'graphique').length },
    'Web': { color: 'bg-cyan-500', count: projects.filter(p => p.category === 'web').length },
    'Vidéo': { color: 'bg-purple-500', count: projects.filter(p => p.category === 'video').length },
    'Audio': { color: 'bg-blue-500', count: projects.filter(p => p.category === 'audio').length }
  };

  // Sub-filters based on tags
  const allTags = [...new Set(projects.flatMap(p => p.tags || []))];

  const filteredProjects = projects.filter(project => {
    const matchesFilter = filter === 'Tous' ||
      (filter === 'Graphique' && project.category === 'graphique') ||
      (filter === 'Web' && project.category === 'web') ||
      (filter === 'Vidéo' && project.category === 'video') ||
      (filter === 'Audio' && project.category === 'audio');

    const matchesSubFilter = !subFilter || project.tags?.includes(subFilter);

    const matchesSearch = !searchQuery ||
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.client?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesFilter && matchesSubFilter && matchesSearch;
  });

  return (
    <div className={noLayout ? "" : "min-h-screen bg-gradient-to-b from-slate-50 to-white"}>
      {!noLayout && <SiteHeader />}

      {/* Hero - Modern design with background carousel */}
      <section className={cn("relative overflow-hidden", noLayout ? "pt-8 pb-16" : "pt-36 md:pt-44 pb-16 md:pb-24")}>
        {/* Background Carousel */}
        <BackgroundCarousel projects={projects.slice(0, 8)} />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          {/* Decorative elements */}
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none" />
          
          <Badge className="mb-4 md:mb-6 bg-white/10 text-white border-white/20 text-xs md:text-sm backdrop-blur-sm">
            ✨ Nos réalisations
          </Badge>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-4 md:mb-6 tracking-tight">
            Portfolio <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">créatif</span>
          </h1>
          
          <p className="text-base md:text-xl text-white/70 max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed">
            Découvrez nos projets les plus marquants avec les objectifs clients et les résultats obtenus.
          </p>
          
          {/* Stats row */}
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            {[
              { value: projects.length || '50+', label: 'Projets réalisés' },
              { value: '98%', label: 'Clients satisfaits' },
              { value: '10+', label: "Années d'expérience" }
            ].map((stat, idx) => (
              <div key={idx} className="text-center">
                <div className="text-2xl md:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-xs md:text-sm text-white/50">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Bottom wave decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-16 md:h-24">
          <svg viewBox="0 0 1440 100" className="w-full h-full" preserveAspectRatio="none">
            <path 
              fill="white" 
              d="M0,50 C360,100 1080,0 1440,50 L1440,100 L0,100 Z"
              className="opacity-100"
            />
          </svg>
        </div>
      </section>

      {/* Filters - Compact on mobile */}
      <section className="py-3 md:py-6 border-b sticky top-16 lg:top-20 bg-white/95 backdrop-blur-xl z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 md:px-4">
          <div className="flex flex-col gap-2 md:gap-4">
            {/* Main Category Filters */}
            <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-center justify-between">
              <div className="flex flex-wrap gap-1.5 md:gap-2 justify-center md:justify-start">
                {Object.entries(CATEGORIES).map(([cat, info]) => (
                  <Button
                    key={cat}
                    variant={filter === cat ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => { setFilter(cat); setSubFilter(''); }}
                    className={cn(
                      "rounded-full gap-1 md:gap-2 transition-all h-7 md:h-9 px-2 md:px-3 text-xs md:text-sm",
                      filter === cat && info.color + " hover:opacity-90"
                    )}
                  >
                    {cat}
                    <span className={cn(
                      "text-[10px] md:text-xs px-1 md:px-1.5 py-0.5 rounded-full",
                      filter === cat ? "bg-white/20" : "bg-slate-100"
                    )}>
                      {info.count}
                    </span>
                  </Button>
                ))}
              </div>
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-8 md:h-10 text-sm"
                />
              </div>
            </div>
            
            {/* Sub-filters by Tags - Hidden on mobile, scrollable on tablet */}
            {allTags.length > 0 && (
              <div className="hidden sm:flex flex-wrap gap-2 items-center overflow-x-auto pb-1">
                <span className="text-xs text-slate-500 font-medium whitespace-nowrap">Filtrer par :</span>
                <Button
                  variant={subFilter === '' ? 'secondary' : 'ghost'}
                  size="sm"
                  onClick={() => setSubFilter('')}
                  className="h-6 md:h-7 text-xs rounded-full"
                >
                  Tous
                </Button>
                {allTags.slice(0, 12).map(tag => (
                  <Button
                    key={tag}
                    variant={subFilter === tag ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => setSubFilter(tag)}
                    className={cn("h-6 md:h-7 text-xs rounded-full whitespace-nowrap", subFilter === tag && "bg-[#06b6d4]/10 text-[#06b6d4]")}
                  >
                    {tag}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Portfolio Grid - 2 columns on mobile */}
      <section className="py-6 md:py-12">
        <div className="max-w-7xl mx-auto px-2 md:px-4">
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
            {filteredProjects.map((project) => (
              <Link
                key={project.id}
                to={`/site/realisations/${encodeURIComponent(project.slug || project.id)}`}
                className="group block"
              >
                <Card className="overflow-hidden border-0 shadow-md md:shadow-lg hover:shadow-xl md:hover:shadow-2xl transition-all duration-500 cursor-pointer h-full">
                <div className="relative aspect-[4/3] overflow-hidden">
                  {project.videoUrl ? (
                    <video src={project.videoUrl} autoPlay muted loop playsInline className="w-full h-full object-cover pointer-events-none" />
                  ) : project.audioUrl ? (
                    <div className="w-full h-full bg-gradient-to-br from-slate-900 to-slate-800 flex flex-col items-center justify-center p-2 md:p-6">
                      <img src={project.image} alt={project.title} className="w-10 md:w-20 h-10 md:h-20 rounded-lg md:rounded-xl object-cover mb-2 md:mb-4 shadow-lg" />
                      <div className="flex items-end justify-center gap-0.5 md:gap-1 h-6 md:h-12">
                        {[...Array(8)].map((_, i) => (
                          <div key={i} className="w-1 md:w-2 bg-gradient-to-t from-cyan-500 to-blue-500 rounded-full animate-pulse" style={{ height: `${20 + Math.random() * 80}%`, animationDelay: `${i * 0.1}s` }} />
                        ))}
                      </div>
                    </div>
                  ) : (
                    <img src={project.image} alt={project.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                  )}
                  <Badge className={cn("absolute top-1.5 left-1.5 md:top-3 md:left-3 text-[10px] md:text-xs px-1.5 md:px-2.5", project.category === 'graphique' && "bg-pink-500", project.category === 'web' && "bg-cyan-500", project.category === 'video' && "bg-purple-500", project.category === 'audio' && "bg-blue-500")}>
                    {project.category === 'graphique' ? 'Graphique' : project.category === 'web' ? 'Web' : project.category === 'video' ? 'Vidéo' : 'Audio'}
                  </Badge>
                  {project.testimonial && (
                    <div className="absolute top-1.5 right-1.5 md:top-3 md:right-3 bg-amber-500 text-white px-1.5 md:px-2 py-0.5 md:py-1 rounded-full text-[9px] md:text-xs flex items-center gap-0.5 md:gap-1">
                      <Star className="w-2.5 md:w-3 h-2.5 md:h-3 fill-white" /> <span className="hidden sm:inline">Avis</span>
                    </div>
                  )}
                </div>
                <CardContent className="p-2 md:p-6">
                  {/* Client with round logo - Compact on mobile */}
                  <div className="flex items-center gap-1.5 md:gap-3 mb-1.5 md:mb-3">
                    {project.clientLogo && (
                      <div className="w-7 h-7 md:w-12 md:h-12 rounded-full overflow-hidden border md:border-2 border-slate-100 shadow flex-shrink-0">
                        <img src={project.clientLogo} alt={project.client} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-[#06b6d4] font-medium text-[10px] md:text-sm truncate">{project.client}</p>
                      <h3 className="font-bold text-xs md:text-lg text-slate-900 line-clamp-2 leading-tight">{project.title}</h3>
                    </div>
                  </div>
                  <p className="text-slate-600 text-[10px] md:text-sm line-clamp-2 mb-2 md:mb-4 hidden sm:block">{project.description}</p>
                  {project.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 md:gap-2">
                      {project.tags.slice(0, 2).map((tag, idx) => (
                        <Badge key={idx} variant="secondary" className="bg-slate-100 text-slate-600 text-[9px] md:text-xs px-1.5 md:px-2 py-0">{tag}</Badge>
                      ))}
                      {project.tags.length > 2 && (
                        <Badge variant="secondary" className="bg-slate-100 text-slate-600 text-[9px] md:text-xs px-1.5 md:px-2 py-0 hidden md:flex">+{project.tags.length - 2}</Badge>
                      )}
                    </div>
                  )}
                </CardContent>
                </Card>
              </Link>
            ))}
            
            {/* CTA Card - "Votre projet mérite d'être ici !" */}
            <Card 
              className="overflow-hidden border-2 border-dashed border-cyan-300 hover:border-cyan-500 shadow-md md:shadow-lg hover:shadow-xl transition-all duration-500 cursor-pointer h-full bg-gradient-to-br from-cyan-50 via-white to-blue-50 group"
              onClick={handleCallbackAction}
              data-testid="cta-your-project-card"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <div className="absolute inset-0 opacity-20">
                  <div className="absolute top-4 left-4 w-20 h-20 border-4 border-white/30 rounded-lg transform rotate-12" />
                  <div className="absolute bottom-8 right-8 w-16 h-16 border-4 border-white/30 rounded-full" />
                  <div className="absolute top-1/2 left-1/2 w-32 h-32 border-4 border-white/20 rounded-xl transform -translate-x-1/2 -translate-y-1/2 rotate-45" />
                </div>
                <div className="text-center z-10 p-4">
                  <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Sparkles className="w-8 h-8 md:w-10 md:h-10 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-sm md:text-xl mb-1">Votre projet</h3>
                  <p className="text-white/90 text-xs md:text-base">mérite d'être ici !</p>
                </div>
                <Badge className="absolute top-1.5 left-1.5 md:top-3 md:left-3 bg-white text-cyan-600 text-[10px] md:text-xs px-1.5 md:px-2.5">
                  Prochain ?
                </Badge>
              </div>
              <CardContent className="p-3 md:p-6">
                <div className="text-center">
                  <p className="text-slate-600 text-[10px] md:text-sm mb-3 md:mb-4">
                    Confiez-nous votre projet et rejoignez nos clients satisfaits
                  </p>
                  <Button 
                    className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-full text-xs md:text-sm group-hover:shadow-lg transition-all"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCallbackAction();
                    }}
                  >
                    <Phone className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-2" />
                    <span className="hidden sm:inline">Être recontacté</span>
                    <span className="sm:hidden">Être rappelé</span>
                    <ArrowRight className="w-3 h-3 md:w-4 md:h-4 ml-1 md:ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-4 mb-2">
                {selectedProject.clientLogo && (
                  <img src={selectedProject.clientLogo} alt={selectedProject.client} className="w-16 h-16 rounded-xl object-cover border shadow" />
                )}
                <div>
                  <p className="text-[#06b6d4] font-medium">{selectedProject.client}</p>
                  <DialogTitle className="text-2xl">{selectedProject.title}</DialogTitle>
                </div>
              </div>
            </DialogHeader>

            {/* Media Display */}
            <div className="my-6">
              {selectedProject.videoUrl || selectedProject.youtubeUrl ? (
                <VideoPlayer videoUrl={selectedProject.videoUrl} youtubeUrl={selectedProject.youtubeUrl} poster={selectedProject.image} />
              ) : selectedProject.audioUrl ? (
                <AudioPlayer audioUrl={selectedProject.audioUrl} coverUrl={selectedProject.image} title={selectedProject.title} />
              ) : selectedProject.category === 'web' ? (
                <WebsitePreview screenshot={selectedProject.fullImage || selectedProject.image} websiteUrl={selectedProject.websiteUrl} title={selectedProject.title} />
              ) : (
                <img src={selectedProject.image} alt={selectedProject.title} className="w-full rounded-xl" />
              )}
            </div>

            {/* Description */}
            <p className="text-slate-600 mb-6">{selectedProject.description}</p>

            {/* Objectives & Results */}
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {selectedProject.objectives && (
                <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5 text-blue-500" /> Objectifs client
                  </h3>
                  <ul className="space-y-2">
                    {selectedProject.objectives.map((obj, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-600 text-sm">
                        <ChevronRight className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        {obj}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {selectedProject.results && (
                <div className="bg-green-50 rounded-xl p-5 border border-green-100">
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-500" /> Résultats obtenus
                  </h3>
                  <ul className="space-y-2">
                    {selectedProject.results.map((result, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-600 text-sm">
                        <ChevronRight className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        {result}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {selectedProject.tags.map((tag, idx) => (
                <Badge key={idx} className="bg-[#06b6d4]/10 text-[#06b6d4]">{tag}</Badge>
              ))}
            </div>

            {/* Testimonial */}
            {selectedProject.testimonial && (
              <TestimonialDisplay testimonial={selectedProject.testimonial} />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Callback Modal */}
      <Dialog open={showCallbackModal} onOpenChange={setShowCallbackModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <Phone className="w-5 h-5 text-cyan-500" />
              Être rappelé
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCallbackSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="callback-name">Nom complet *</Label>
              <Input
                id="callback-name"
                placeholder="Votre nom"
                value={callbackForm.name}
                onChange={(e) => setCallbackForm({ ...callbackForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="callback-phone">Téléphone *</Label>
              <Input
                id="callback-phone"
                type="tel"
                placeholder="06 00 00 00 00"
                value={callbackForm.phone}
                onChange={(e) => setCallbackForm({ ...callbackForm, phone: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="callback-message">Message (optionnel)</Label>
              <Textarea
                id="callback-message"
                placeholder="Parlez-nous brièvement de votre projet..."
                value={callbackForm.message}
                onChange={(e) => setCallbackForm({ ...callbackForm, message: e.target.value })}
                rows={3}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCallbackModal(false)} 
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600"
                disabled={submittingCallback}
              >
                {submittingCallback ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Phone className="w-4 h-4 mr-2" />
                    Demander un rappel
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#1a365d] to-[#2d4a7c]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Votre projet mérite d&apos;être ici
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Discutons de votre projet et créons ensemble quelque chose d&apos;exceptionnel.
          </p>
          <Link to="/site#contact">
            <Button size="lg" className="bg-[#06b6d4] hover:bg-[#0891b2] text-white">
              Demander un devis gratuit
            </Button>
          </Link>
        </div>
      </section>

      {!noLayout && <SiteFooter />}
    </div>
  );
};

export default PortfolioPage;
